import type { GameId, LevelId, MistakeType, TeachingLanguage } from '@/domain/ids';
import type { ContentItem, Lesson, LearningObjective, Localized, Practice } from '@/content/schema';

/**
 * Small authoring helpers so content files stay readable. They only build
 * plain data objects; the pack is validated with zod when it is loaded.
 */

/** Every item in this pack is sample content awaiting educator validation. */
export const SAMPLE = { status: 'published', verification: 'unverified-sample' } as const;

export const MOE_REF = [
  { sourceId: 'tn-moe-programmes', note: 'Programme listed; content not yet checked against it.' },
];

type OptionInput = string | { text: string; emoji?: string; mistake?: MistakeType };

/** First option is the correct one; ids are assigned here and shuffled at runtime. */
export function opts(correct: OptionInput, ...wrong: OptionInput[]) {
  const ids = ['a', 'b', 'c', 'd', 'e', 'f'];
  const toOption = (o: OptionInput, i: number) =>
    typeof o === 'string' ? { id: ids[i] as string, text: o } : { id: ids[i] as string, ...o };
  return { options: [correct, ...wrong].map(toOption), answer: 'a' };
}

/** Distractor tagged with the mistake it reveals. */
export const m = (text: string, mistake: MistakeType, emoji?: string) => ({
  text,
  mistake,
  ...(emoji ? { emoji } : {}),
});

export interface ObjectiveInput {
  id: string;
  domainId: string;
  levels: LevelId[];
  order: number;
  title: Localized;
  description: Localized;
  difficulty: number;
  prerequisites?: string[];
  practice?: Practice;
  games: GameId[];
}

export function objective(
  subjectId: string,
  lang: TeachingLanguage,
  input: ObjectiveInput,
): LearningObjective {
  return {
    subjectId,
    teachingLanguage: lang,
    prerequisites: [],
    practice: { type: 'bank' },
    sources: MOE_REF,
    ...SAMPLE,
    ...input,
  };
}

export function lesson(objectiveId: string, title: string, steps: Lesson['steps']): Lesson {
  return { id: `${objectiveId}.lesson`, objectiveId, title, steps, ...SAMPLE };
}

type ItemInput = ContentItem extends infer T
  ? T extends ContentItem
    ? Omit<T, 'objectiveId' | 'status' | 'verification' | 'difficulty' | 'id'> & {
        difficulty?: number;
      }
    : never
  : never;

/** Builds the item bank of one objective with ids `<objective>.<n>`. */
export function bank(objectiveId: string, difficulty: number, items: ItemInput[]): ContentItem[] {
  return items.map(
    (item, i) =>
      ({
        difficulty,
        ...item,
        id: `${objectiveId}.${i + 1}`,
        objectiveId,
        ...SAMPLE,
      }) as ContentItem,
  );
}
